import React, { useState, useEffect } from 'react';
import useCarehomePartners from '@hooks/useCarehomePartners';
import { usePartner } from '@contexts/PartnerContext';
import { capitalizeName } from '@utils/commons';
import useGetCarehomeInfo from '@hooks/useGetCarehomeInfo';
import './styles.scss';

interface CarehomeDetails {
  name: String;
  img: string;
  totalResidents: number;
}

type CarehomeAddress = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  county?: string;
  postCode?: string;
};

interface AdvanceCarePlanningProps {
  title: string;
  showExtraContent?: boolean;
  // eslint-disable-next-line react/require-default-props
  setTotalResidents?: (total: number) => void;
  // eslint-disable-next-line react/require-default-props
  setCarePlaceName?: (name: string) => void;
}

const AdvanceCarePlanning: React.FC<AdvanceCarePlanningProps> = ({
  title,
  showExtraContent = false,
  setTotalResidents,
  setCarePlaceName,
}) => {
  const { selectedPartnerId: parnerId, carehomePartnerList } =
    useCarehomePartners();
  const { selectedPartnerId, setSelectedPartnerId } = usePartner();

  const [carePlaceDetails, setCarePlaceDetails] = useState<CarehomeDetails>({
    name: '',
    img: '',
    totalResidents: 0,
  });
  const { savedCarehomeAddressFromTeams, phoneNumber } =
    useGetCarehomeInfo(selectedPartnerId);

  useEffect(() => {
    if (parnerId) {
      setSelectedPartnerId(parnerId);
    }
  }, [carehomePartnerList, parnerId, setSelectedPartnerId]);

  useEffect(() => {
    const selectedPartner =
      carehomePartnerList.find(partner => partner.id === selectedPartnerId) ??
      null;
    if (selectedPartner) {
      const { name, image: img, totalResidents } = selectedPartner;

      setCarePlaceDetails({
        name,
        img,
        totalResidents,
      });
      if (name && setCarePlaceName) {
        setCarePlaceName(name);
      }

      if (setTotalResidents) {
        setTotalResidents(totalResidents);
      }
    }
  }, [
    carehomePartnerList,
    selectedPartnerId,
    setCarePlaceName,
    setTotalResidents,
  ]);

  const formatCarehomeAddress = (address: CarehomeAddress): string => {
    if (!address) return '';

    return [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.county,
    ]
      .filter(Boolean)
      .join(', ');
  };

  return (
    <div className="mt-10 govuk-bp:mt-14">
      <h1 className="govuk-heading-l homepage-acp-title py-2 px-3">{title}</h1>
      <div className="care-place-info flex justify-between">
        <div>
          <h1 className="govuk-heading-l underline">
            {capitalizeName(carePlaceDetails.name.toString())}
          </h1>
          <h3 className="govuk-heading-s">
            {formatCarehomeAddress(savedCarehomeAddressFromTeams)}
          </h3>
          <h3 className="govuk-heading-s">
            <p className="govuk-body underline govuk-!-margin-0 govuk-!-font-weight-bold">
              {phoneNumber ? (
                <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
              ) : (
                ''
              )}
            </p>
          </h3>
          <h3 className="govuk-heading-s underline current-case-load">
            Current Case Load: {carePlaceDetails.totalResidents}
          </h3>
        </div>
        <img
          src={carePlaceDetails.img}
          alt="CareHome"
          width="200"
          height="200"
          className="care-place-img"
        />
      </div>
      {showExtraContent && (
        <div className="mt-10">
          <h1 className="govuk-heading-l">
            {capitalizeName(carePlaceDetails.name.toString())} - Analytics
            Dashboard
          </h1>
          <p className="govuk-body">
            The Analytics dashboard visualises data in realtime. Data provided
            can be used for ongoing internal and external review, assessment and
            improvement.
          </p>
        </div>
      )}
    </div>
  );
};

AdvanceCarePlanning.defaultProps = {
  showExtraContent: false,
};

export default AdvanceCarePlanning;
